import React from 'react';

const Loader = ({ size = 56, color = '#ff6b35' }) => (
  <div
    style={{
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: `${size}px`,
      height: `${size}px`,
    }}
  >
    <style>{`
      @keyframes ripple {
        0% {
          transform: scale(0.8);
          opacity: 0.8;
        }
        100% {
          transform: scale(1.6);
          opacity: 0;
        }
      }

      @keyframes loaderFlip {
        0%, 80% {
          transform: translate(13.75px, 9.25px) rotate(0deg);
          animation-timing-function: cubic-bezier(0.68, -0.55, 0.27, 1.55);
        }
        95%, 100% {
          transform: translate(13.75px, 9.25px) rotate(180deg);
        }
      }

      @keyframes glareTop {
        0%, 80% {
          stroke: rgba(255, 255, 255, 1);
          animation-timing-function: ease-in-out;
        }
        95%, 100% {
          stroke: rgba(255, 255, 255, 0);
        }
      }

      @keyframes glareBottom {
        0%, 80% {
          stroke: rgba(255, 255, 255, 0);
          animation-timing-function: ease-in-out;
        }
        95%, 100% {
          stroke: rgba(255, 255, 255, 1);
        }
      }

      @keyframes sandDrop {
        0%, 10% {
          stroke-dashoffset: 1;
          animation-timing-function: cubic-bezier(0.68, -0.55, 0.27, 1.55);
          opacity: 1;
        }
        75%, 100% {
          stroke-dashoffset: -107;
          opacity: 0.3;
        }
      }

      @keyframes sandFill {
        0%, 10% {
          stroke-dashoffset: 55;
          animation-timing-function: cubic-bezier(0.68, -0.55, 0.27, 1.55);
        }
        75%, 100% {
          stroke-dashoffset: -54;
        }
      }

      @keyframes sandGrainLeft {
        0%, 10% {
          stroke-dashoffset: 29;
          animation-timing-function: cubic-bezier(0.68, -0.55, 0.27, 1.55);
        }
        75%, 100% {
          stroke-dashoffset: -22;
        }
      }

      @keyframes sandGrainRight {
        0%, 10% {
          stroke-dashoffset: 27;
          animation-timing-function: cubic-bezier(0.68, -0.55, 0.27, 1.55);
        }
        75%, 100% {
          stroke-dashoffset: -24;
        }
      }

      @keyframes sandLineLeft {
        0%, 10% {
          stroke-dashoffset: 53;
          animation-timing-function: cubic-bezier(0.68, -0.55, 0.27, 1.55);
        }
        75%, 100% {
          stroke-dashoffset: -55;
        }
      }

      @keyframes sandLineRight {
        0%, 10% {
          stroke-dashoffset: 14;
          animation-timing-function: cubic-bezier(0.68, -0.55, 0.27, 1.55);
        }
        75%, 100% {
          stroke-dashoffset: -24.5;
        }
      }

      @keyframes sandMoundTop {
        0% {
          transform: translateY(0);
          opacity: 1;
          animation-timing-function: cubic-bezier(0.68, -0.55, 0.27, 1.55);
        }
        55% {
          transform: translateY(8px);
          opacity: 0.65;
        }
        90%, 100% {
          transform: translateY(14px);
          opacity: 0;
        }
      }

      @keyframes sandMoundBottom {
        0%, 20% {
          transform: scaleY(0);
          opacity: 0;
          animation-timing-function: cubic-bezier(0.68, -0.55, 0.27, 1.55);
        }
        90%, 100% {
          transform: scaleY(1);
          opacity: 1;
        }
      }

      .animated-circle {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border: 2px solid;
        border-radius: 50%;
        border-color: rgba(59, 130, 246, 0.6);
        animation: ripple 2.5s ease-out infinite;
        animation-delay: 0.5s;
        z-index: 0;
        will-change: transform, opacity;
      }

      .animated-circle:nth-child(2) {
        animation-delay: 1.75s;
        border-color: rgba(45, 212, 191, 0.5);
      }

      .loader__glare-top,
      .loader__glare-bottom,
      .loader__model,
      .loader__sand-drop,
      .loader__sand-fill,
      .loader__sand-grain-left,
      .loader__sand-grain-right,
      .loader__sand-line-left,
      .loader__sand-line-right,
      .loader__sand-mound-top,
      .loader__sand-mound-bottom {
        animation-duration: 2.5s;
        animation-iteration-count: infinite;
        animation-timing-function: ease-in-out;
        will-change: transform, stroke-dashoffset, opacity;
      }

      .loader__glare-top {
        animation-name: glareTop;
      }

      .loader__glare-bottom {
        animation-name: glareBottom;
      }

      .loader__model {
        animation-name: loaderFlip;
        transform-origin: 12.25px 16.75px;
        will-change: transform;
      }

      .loader__sand-drop {
        animation-name: sandDrop;
      }

      .loader__sand-fill {
        animation-name: sandFill;
      }

      .loader__sand-grain-left {
        animation-name: sandGrainLeft;
      }

      .loader__sand-grain-right {
        animation-name: sandGrainRight;
      }

      .loader__sand-line-left {
        animation-name: sandLineLeft;
      }

      .loader__sand-line-right {
        animation-name: sandLineRight;
      }

      .loader__sand-mound-top {
        animation-name: sandMoundTop;
        transform-origin: 12.25px 14px;
      }

      .loader__sand-mound-bottom {
        animation-name: sandMoundBottom;
        transform-origin: 12.25px 31.5px;
      }
    `}</style>
    
    <div className="animated-circle" />
    <div className="animated-circle" />
    
    <svg
      aria-label="Sand timer loader animation"
      role="img"
      height={size}
      width={size}
      viewBox="0 0 56 56"
      className="loader"
      style={{ position: 'relative', zIndex: 1 }}
    >
      <defs>
        <clipPath id="sand-mound-top">
          <path
            d="M 14.613 13.087 C 15.814 12.059 19.3 8.039 20.3 6.539 C 21.5 4.789 21.5 2.039 21.5 2.039 
            L 3 2.039 C 3 2.039 3 4.789 4.2 6.539 C 5.2 8.039 8.686 12.059 9.887 13.087 
            C 11 14.039 12.25 14.039 12.25 14.039 C 12.25 14.039 13.5 14.039 14.613 13.087 Z"
            className="loader__sand-mound-top"
          />
        </clipPath>
        <clipPath id="sand-mound-bottom">
          <path
            d="M 14.613 20.452 C 15.814 21.48 19.3 25.5 20.3 27 C 21.5 28.75 21.5 31.5 21.5 31.5 
            L 3 31.5 C 3 31.5 3 28.75 4.2 27 C 5.2 25.5 8.686 21.48 9.887 20.452 
            C 11 19.5 12.25 19.5 12.25 19.5 C 12.25 19.5 13.5 19.5 14.613 20.452 Z"
            className="loader__sand-mound-bottom"
          />
        </clipPath>
      </defs>
      <g transform="translate(2,2)">
        <g transform="translate(13.75,9.25)" className="loader__model">
          <path
            d="M 1.5 2 L 23 2 C 23 2 22.5 8.5 19 12 C 16 15.5 13.5 13.5 13.5 16.75 
            C 13.5 20 16 18 19 21.5 C 22.5 25 23 31.5 23 31.5 L 1.5 31.5 C 1.5 31.5 2 25 5.5 21.5 
            C 8.5 18 11 20 11 16.75 C 11 13.5 8.5 15.5 5.5 12 C 2 8.5 1.5 2 1.5 2 Z"
            fill="hsl(210, 15%, 92%)"
          />
          {/* Sand animation */}
          <g strokeLinecap="round" stroke="#3b82f6">
            <line
              y2="20.75"
              x2="12"
              y1="15.75"
              x1="12"
              strokeDasharray="0.25 20.75"
              strokeWidth="1"
              className="loader__sand-grain-left"
            />
            <line
              y2="21.75"
              x2="12.5"
              y1="16.75"
              x1="12.5"
              strokeDasharray="0.25 20.75"
              strokeWidth="1"
              className="loader__sand-grain-right"
            />
            <line
              y2="31.5"
              x2="12.25"
              y1="18"
              x1="12.25"
              strokeDasharray="0.5 107.5"
              strokeWidth="1"
              className="loader__sand-drop"
            />
            <line
              y2="31.5"
              x2="12.25"
              y1="14.75"
              x1="12.25"
              strokeDasharray="54 54"
              strokeWidth="1.5"
              className="loader__sand-fill"
            />
            <line
              y2="31.5"
              x2="12"
              y1="16"
              x1="12"
              strokeDasharray="1 107"
              strokeWidth="1"
              stroke="#2563eb"
              className="loader__sand-line-left"
            />
            <line
              y2="31.5"
              x2="12.5"
              y1="16"
              x1="12.5"
              strokeDasharray="12 96"
              strokeWidth="1"
              stroke="#2563eb"
              className="loader__sand-line-right"
            />
            <g strokeWidth="0" fill="#3b82f6">
              <path
                d="M 12.25 15 L 15.392 13.486 C 21.737 11.168 22.5 2 22.5 2 L 2 2.013 
                C 2 2.013 2.753 11.046 9.009 13.438 L 12.25 15 Z"
                clipPath="url(#sand-mound-top)"
              />
              <path
                d="M 12.25 18.5 L 15.392 20.014 C 21.737 22.332 22.5 31.5 22.5 31.5 
                L 2 31.487 C 2 31.487 2.753 22.454 9.009 20.062 Z"
                clipPath="url(#sand-mound-bottom)"
              />
            </g>
          </g>
          {/* Glare animation */}
          <g strokeWidth="2" strokeLinecap="round" opacity="0.7" fill="none">
            <path
              d="M 19.437 3.421 C 19.437 3.421 19.671 6.454 17.914 8.846 C 16.157 11.238 14.5 11.5 14.5 11.5"
              stroke="hsl(0,0%,100%)"
              className="loader__glare-top"
            />
            <path
              transform="rotate(180,12.25,16.75)"
              d="M 19.437 3.421 C 19.437 3.421 19.671 6.454 17.914 8.846 C 16.157 11.238 14.5 11.5 14.5 11.5"
              stroke="hsla(0,0%,100%,0)"
              className="loader__glare-bottom"
            />
          </g>
          {/* Hourglass edges */}
          <rect height="2" width="24.5" fill="#3b82f6" />
          <rect
            height="1"
            width="19.5"
            y="0.5"
            x="2.5"
            ry="0.5"
            rx="0.5"
            fill="#3b82f6"
            opacity="0.8"
          />
          <rect
            height="2"
            width="24.5"
            y="31.5"
            fill="#2dd4bf"
          />
          <rect
            height="1"
            width="19.5"
            y="32"
            x="2.5"
            ry="0.5"
            rx="0.5"
            fill="#2dd4bf"
            opacity="0.8"
          />
        </g>
      </g>
    </svg>
  </div>
);

export default React.memo(Loader);